import React from 'react';
import RiskGraph from './RiskGraph';
import { 
  formatDatePart, 
  calculateDTE, 
  formatSpread, 
  formatFinancialValueCell,
  calculateIVSkew,
  calculateStrikeDifference,
  getValColor,
  getHedgeColor
} from '../utils/formatters';
import osLogo from '../os-logo.png';

const getFirstExpiry = (item) => {
  const exps = [];
  const pushExp = (e) => { if (e) exps.push(e); };
  (item?.calendar_spread?.legs || []).forEach(l => pushExp(l?.expiration));
  (item?.diagonal_spread?.legs || []).forEach(l => pushExp(l?.expiration));
  (item?.vertical_spread?.legs || []).forEach(l => pushExp(l?.expiration));
  const dates = exps
    .map(s => {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d;
    })
    .filter(Boolean);
  if (dates.length === 0) return null;
  return new Date(Math.min.apply(null, dates));
};

const formatYyyymmdd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${da}`;
};

const Metric = ({ label, value, color = 'inherit', suffix = '' }) => (
  <div className="card-metric">
    <div className="card-metric-label">{label}</div>
    <div className="card-metric-value" style={{ color }}>
      {value}{suffix}
    </div>
  </div>
);

const StarIcon = ({ filled }) => (
  filled ? (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <polygon
        points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  )
);

const CombinationCard = ({ item, isSelected, onSelect, isPinned, onTogglePin, isStale }) => {
  const firstExp = item.calendar_spread?.legs[0]?.expiration;
  const secondExp = item.calendar_spread?.legs[1]?.expiration;
  const dte = calculateDTE(firstExp);
  const ivSkew = calculateIVSkew(item);
  const strikeDiff = calculateStrikeDifference(item);
  
  const handleTosDownload = (e) => {
    e.stopPropagation();
    import('../utils/tos').then(mod => {
      const content = mod.buildTosFileContent(item);
      if (!content) return;
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const firstExpDate = getFirstExpiry(item);
      const suffix = firstExpDate ? `_${formatYyyymmdd(firstExpDate)}` : '';
      a.download = `fusion${suffix}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };
  
  // Hedge logic (negative is green profit)
  const hedgeVal = item.costs?.net_strategy_cost_after_vertical_credit;
  let hedgeDisplay = hedgeVal;
  if (typeof hedgeVal === 'number' && hedgeVal !== 0) {
    hedgeDisplay = hedgeVal < 0 ? Math.abs(hedgeVal) : -hedgeVal;
  }
  
  const safeId = typeof item.id === 'string' ? item.id : '';
  const fusionNumber = safeId.startsWith('combo-')
    ? parseInt(safeId.replace('combo-', ''), 10) + 1
    : null;

  return (
    <div 
      className={`combination-card ${isSelected ? 'selected' : ''} ${isPinned ? 'is-pinned' : ''} ${isStale ? 'is-stale' : ''}`.trim()} 
      onClick={onSelect}
    >
      <div className="card-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            {fusionNumber !== null && <span className="card-badge">Fusion {fusionNumber}</span>}
            <span className="card-sub">DTE: {dte} ({formatDatePart(firstExp)}/{formatDatePart(secondExp)})</span>
            {isStale && <span className="card-stale-badge" title="This is a stale snapshot — the combination is no longer in the latest scan.">stale</span>}
          </div>
          
          <div className="card-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {typeof onTogglePin === 'function' && (
              <button
                type="button"
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
                className={`card-pin-btn ${isPinned ? 'is-pinned' : ''}`}
                title={isPinned ? 'Unpin' : 'Pin this combination'}
                aria-pressed={!!isPinned}
                aria-label={isPinned ? 'Unpin' : 'Pin'}
              >
                <StarIcon filled={!!isPinned} />
              </button>
            )}
            {item.optionStratLink && (
              <a 
                href={item.optionStratLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={(e) => e.stopPropagation()}
                title="Open in OptionStrat"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <img src={osLogo} alt="OS" style={{ width: '18px', height: 'auto' }} />
              </a>
            )}
            <button
              onClick={handleTosDownload}
              title="Download for TOS"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <img src="https://www.opcioguru.com/static/trading2/img/thinkorswim.png" alt="TOS" style={{ width: '18px', height: 'auto' }} />
            </button>
          </div>
        </div>
        
        <div className="card-metrics-grid">
          <Metric 
            label="CAL" 
            value={formatSpread(item.calendar_spread, 'calendar', item.calendar_quantity)} 
          />
          <Metric 
            label="DIAG" 
            value={formatSpread(item.diagonal_spread, 'diagonal', item.diagonal_quantity)} 
          />
          <Metric 
            label="H-STRAT" 
            value={formatSpread(item.vertical_spread, 'vertical')} 
          />
          <Metric 
            label="R/R" 
            value={item.rr_ratio} 
            suffix="%" 
            color="var(--accent)"
          />
          <Metric 
            label="NET Δ" 
            value={formatFinancialValueCell(item.net_delta, false, 2)} 
            color={getValColor(item.net_delta)}
          />
          <Metric 
            label="VOD" 
            value={formatFinancialValueCell(item.min_pl_death_valley, true, 0)} 
            color={getValColor(item.min_pl_death_valley)}
          />
          <Metric 
            label="HEDGE" 
            value={formatFinancialValueCell(hedgeDisplay, true, 0)} 
            color={getHedgeColor(hedgeVal)}
          />
          <Metric 
            label="COST" 
            value={formatFinancialValueCell(item.total_debit, true, 0)} 
          />
          <Metric 
            label="SKEW" 
            value={typeof ivSkew === 'number' ? ivSkew.toFixed(2) : ivSkew} 
            suffix={typeof ivSkew === 'number' ? '%' : ''}
          />
          <Metric 
            label="DIFF" 
            value={strikeDiff} 
          />
        </div>
      </div>
      
      <div className="card-body">
        <RiskGraph selectedCombination={item} />
      </div>
    </div>
  );
};

export default CombinationCard;
